import * as React from 'react';
import {Select} from './Select';
import type {PreviewSelectProps, SelectOptions} from './_types';
import {fixedForwardRef} from '../../utils';

const PreviewSelectBase = <TOptions extends SelectOptions>(
  {
    isOpen: isOpenProp,
    onChange: onChangeProp,
    onRequestClose: onRequestCloseProp,
    onRequestOpen: onRequestOpenProp,
    value: valueProp,
    ...props
  }: PreviewSelectProps<TOptions>,
  ref: React.Ref<HTMLDivElement>
) => {
  const [value, setValue] = React.useState(valueProp);

  const [isOpenState, setIsOpenState] = React.useState(false);
  const isOpen = isOpenProp ?? isOpenState;

  const closeSelect = (event?: Event | React.SyntheticEvent) => {
    setIsOpenState(false);
    onRequestCloseProp?.(event);
  };

  const openSelect = (event?: Event | React.SyntheticEvent) => {
    setIsOpenState(true);
    onRequestOpenProp?.(event);
  };

  // Need to sync only when the Select is closed.
  React.useEffect(() => {
    if (!isOpen && valueProp !== value) {
      setValue(valueProp);
    }
  }, [isOpen, valueProp]);

  const handleChange: PreviewSelectProps<TOptions>['onChange'] = (
    newValue,
    meta
  ) => {
    onChangeProp(newValue, meta);
    setValue(valueProp);
  };

  return (
    <Select
      {...props}
      _shouldUseLegacyAlignment
      isOpen={isOpen}
      onChange={handleChange}
      onPreselect={onChangeProp}
      onRequestClose={closeSelect}
      onRequestOpen={openSelect}
      ref={ref}
      shouldPositionInline
      value={value}
    />
  );
};

export const PreviewSelect = fixedForwardRef(PreviewSelectBase);
